import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { ZefForm, ZefFormsManagerService } from '@zerops/zef/forms';
import { AppState } from '@zerops/zerops/app';
import { getNextServiceName } from '@zerops/zerops/core/shared/service-name.utils';
import {
  FormControlState,
  FormGroupState,
  validate,
  setErrors
} from 'ngrx-forms';
import { required } from 'ngrx-forms/validation';
import { FEATURE_NAME, ZCP_SERVICE_NAME_PREFIX } from './control-plane-dialog.constant';
import {
  ZeropsYamlBuilder,
  YamlAgentConfig,
  generateSecurePassword,
  ZagentAgentType,
  ZagentAuthType
} from '@zerops/zerops/core/zerops-services';

/**
 * Per-agent fields are keyed by an agent suffix (`ClaudeCode`, `Codex`,
 * `Antigravity`, `Grok`, `Cursor`) on each field family. ngrx-forms needs
 * a static flat shape — no dynamic keys, no nested groups — so each agent
 * gets its own trio of explicit fields (enable / authType / apiToken).
 *
 * The legacy `agentType` / `authType` / `provider` / `apiToken` quartet
 * is retained as mirror fields that shadow the *primary* (first-enabled)
 * agent. Downstream consumers — `extractZagentConfigFromForm` callers in
 * onboarding and dashboard-project-add — still read these. Phase 1G drops
 * them once those consumers migrate to a multi-agent shape.
 */
export interface ControlPlaneFormValue {
  enableAgent: boolean;
  enableVscode: boolean;

  // -- Per-agent fields. Every agent supports both OAuth (subscription
  // login) and token modes via their respective providers; the form +
  // YAML wire them identically.
  enableAgentClaudeCode: boolean;
  enableAgentCodex: boolean;
  enableAgentAntigravity: boolean;
  enableAgentGrok: boolean;
  enableAgentCursor: boolean;

  authTypeClaudeCode: ZagentAuthType;
  authTypeCodex: ZagentAuthType;
  authTypeAntigravity: ZagentAuthType;
  authTypeGrok: ZagentAuthType;
  authTypeCursor: ZagentAuthType;

  apiTokenClaudeCode: string;
  apiTokenCodex: string;
  apiTokenAntigravity: string;
  apiTokenGrok: string;
  apiTokenCursor: string;

  // -- Legacy mirror — primary (first-enabled) agent. Dropped in 1G.
  agentType: ZagentAgentType;
  authType: ZagentAuthType;
  provider: string;
  apiToken: string;

  enablePublicAccess: boolean;
  vscodePassword: string;
  selectedServices: boolean[];
}

export type ControlPlaneFormState = FormGroupState<ControlPlaneFormValue>;

/**
 * Order in which to pick the "primary" agent for the legacy mirror fields.
 * Claude-first preserves today's default behavior for existing deploys.
 */
export const PRIMARY_AGENT_ORDER: ZagentAgentType[] = [
  'claude-code',
  'codex',
  'antigravity',
  'grok',
  'cursor'
];

const DEFAULT_VALUES: ControlPlaneFormValue = {
  enableAgent: true,
  enableVscode: true,

  enableAgentClaudeCode: true,
  enableAgentCodex: false,
  enableAgentAntigravity: false,
  enableAgentGrok: false,
  enableAgentCursor: false,

  authTypeClaudeCode: 'oauth',
  authTypeCodex: 'oauth',
  authTypeAntigravity: 'oauth',
  authTypeGrok: 'oauth',
  authTypeCursor: 'oauth',

  apiTokenClaudeCode: '',
  apiTokenCodex: '',
  apiTokenAntigravity: '',
  apiTokenGrok: '',
  apiTokenCursor: '',

  agentType: 'claude-code',
  authType: 'oauth',
  provider: 'anthropic',
  apiToken: '',

  enablePublicAccess: true,
  vscodePassword: generateSecurePassword(),
  selectedServices: []
};

/**
 * Reduce the per-agent form fields to a list of enabled `ZagentAgentType`s
 * in primary-priority order. Used by YAML build, deploy meta, and the
 * legacy-mirror sync.
 */
export function enabledAgentsFromForm(v: ControlPlaneFormValue): ZagentAgentType[] {
  return PRIMARY_AGENT_ORDER.filter((t) => isAgentEnabled(v, t));
}

export function isAgentEnabled(v: ControlPlaneFormValue, t: ZagentAgentType): boolean {
  switch (t) {
    case 'claude-code': return v.enableAgentClaudeCode;
    case 'codex': return v.enableAgentCodex;
    case 'antigravity': return v.enableAgentAntigravity;
    case 'grok': return v.enableAgentGrok;
    case 'cursor': return v.enableAgentCursor;
    default: return false;
  }
}

export function getAgentAuthType(v: ControlPlaneFormValue, t: ZagentAgentType): ZagentAuthType {
  switch (t) {
    case 'claude-code': return v.authTypeClaudeCode;
    case 'codex': return v.authTypeCodex;
    case 'antigravity': return v.authTypeAntigravity;
    case 'grok': return v.authTypeGrok;
    case 'cursor': return v.authTypeCursor;
    default: return 'oauth';
  }
}

export function getAgentApiToken(v: ControlPlaneFormValue, t: ZagentAgentType): string {
  switch (t) {
    case 'claude-code': return v.apiTokenClaudeCode;
    case 'codex': return v.apiTokenCodex;
    case 'antigravity': return v.apiTokenAntigravity;
    case 'grok': return v.apiTokenGrok;
    case 'cursor': return v.apiTokenCursor;
    default: return '';
  }
}

@Injectable({ providedIn: 'root' })
export class ControlPlaneForm extends ZefForm<ControlPlaneFormValue> {
  #yamlBuilder = inject(ZeropsYamlBuilder);

  constructor(
    public store: Store<AppState>,
    public formsManager: ZefFormsManagerService
  ) {
    // Per-agent validator factory: token-mode requires a token when the
    // agent is enabled AND the master Coding Agent toggle is on.
    const tokenRequired = (
      enabledKey: keyof ControlPlaneFormValue,
      authKey: keyof ControlPlaneFormValue
    ) => (s: FormControlState<string>, ps: FormGroupState<ControlPlaneFormValue>) => {
      if (
        ps?.controls?.enableAgent.value
        && (ps?.controls?.[enabledKey].value as boolean)
        && (ps?.controls?.[authKey].value as ZagentAuthType) === 'token'
      ) {
        return validate(s, required);
      }
      return setErrors(s, {});
    };

    super(
      store,
      formsManager,
      FEATURE_NAME,
      DEFAULT_VALUES,
      {
        enableAgent: validate(required),
        enableVscode: validate(required),
        apiTokenClaudeCode: tokenRequired('enableAgentClaudeCode', 'authTypeClaudeCode'),
        apiTokenCodex: tokenRequired('enableAgentCodex', 'authTypeCodex'),
        apiTokenAntigravity: tokenRequired('enableAgentAntigravity', 'authTypeAntigravity'),
        apiTokenGrok: tokenRequired('enableAgentGrok', 'authTypeGrok'),
        apiTokenCursor: tokenRequired('enableAgentCursor', 'authTypeCursor'),
        vscodePassword: (s: FormControlState<string>, ps: FormGroupState<ControlPlaneFormValue>) => {
          if (ps?.controls?.enableVscode.value && ps?.controls?.enablePublicAccess.value) {
            return validate(s, required);
          }
          return setErrors(s, {});
        }
      }
    );
  }

  getDefaultValues(): ControlPlaneFormValue {
    return {
      ...DEFAULT_VALUES,
      vscodePassword: generateSecurePassword()
    };
  }

  generateNewPassword(): string {
    return generateSecurePassword();
  }

  generateYamlContent(
    formValue: ControlPlaneFormValue,
    services: any[] = [],
    existingServiceNames: string[] = []
  ): string {
    const {
      enableAgent,
      enableVscode,
      enablePublicAccess,
      vscodePassword,
      selectedServices
    } = formValue;

    const serviceName = getNextServiceName(ZCP_SERVICE_NAME_PREFIX, existingServiceNames);

    const selectedHostnames = selectedServices
      ?.map((isSelected, index) => isSelected ? services[index]?.hostname : null)
      .filter((hostname): hostname is string => hostname !== null) ?? [];

    const agents: YamlAgentConfig[] = enableAgent
      ? enabledAgentsFromForm(formValue).map((t) => ({
        agentType: t,
        authType: getAgentAuthType(formValue, t),
        apiToken: getAgentApiToken(formValue, t)
      }))
      : [];

    return this.#yamlBuilder.generate({
      serviceName,
      enableAgent,
      enableVscode,
      agents,
      enablePublicAccess,
      vscodePassword: enableVscode && enablePublicAccess ? vscodePassword : undefined,
      selectedServices: selectedHostnames
    });
  }
}
